import { MonitorDownIcon, SquarePlusIcon, type LucideIcon } from "lucide-react"
import { useState, useSyncExternalStore } from "react"
import { InstallCallout, InstallSheet, useViewport } from "@/design-system/index.ts"
import { homeOffers, installRoute, offersInstall } from "./install-route.ts"

/*
 * The offer to install Beacon, wherever this browser can (`install-route.ts` decides how): "Install
 * Beacon" in the avatar's drawer, the account menu and the palette, and once, on the compact
 * layout, a card on Home. On Chromium it opens the browser's own prompt, which `main.tsx` keeps
 * from the moment the browser offers it; on iOS a sheet shows the Share sheet's two steps; inside
 * Slack's or another app's browser the sheet says to open Beacon in the phone's own browser. In the
 * installed app, none of it shows. How the sheet and the card look is the design system's
 * (`InstallSheet`, `InstallCallout`); this is when they show.
 */

/** Chromium's install prompt, held back from the browser until the Member asks for it. */
type InstallPrompt = Event & { prompt: () => Promise<void> }

type Offer = {
  /** The prompt Chromium handed over, until it is used. */
  prompt: InstallPrompt | undefined
  /** Beacon was installed from this tab, which itself stays a tab. */
  installed: boolean
  /** The sheet with the steps, or the line about another app's browser, is open. */
  sheetOpen: boolean
}

let offer: Offer = { prompt: undefined, installed: false, sheetOpen: false }
const listeners = new Set<() => void>()

function set(next: Partial<Offer>) {
  offer = { ...offer, ...next }
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => void listeners.delete(listener)
}

/** Where Home's card is remembered as put away, in this browser. */
const cardKey = "beacon:install-card-dismissed"

function cardDismissed() {
  try {
    return localStorage.getItem(cardKey) !== null
  } catch {
    return false
  }
}

function dismissCard() {
  try {
    localStorage.setItem(cardKey, "1")
  } catch {
    // Without storage the card comes back on the next visit; it is still put away for this one.
  }
}

/**
 * Keeps Chromium's install prompt from showing by itself, for "Install Beacon" to open, and hears
 * Beacon being installed. `main.tsx` calls it before anything renders: the browser offers the
 * prompt once, as soon as the page can be installed.
 */
export function listenForInstall() {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault()
    set({ prompt: event as InstallPrompt })
  })
  window.addEventListener("appinstalled", () => {
    dismissCard()
    set({ prompt: undefined, installed: true })
  })
}

/** Installs Beacon the way this browser can: its own prompt, or the sheet. */
function install() {
  const { prompt } = offer
  if (!prompt) return set({ sheetOpen: true })
  // A prompt opens once; the browser offers another if the Member says no.
  set({ prompt: undefined })
  void prompt.prompt()
}

/**
 * How this browser can install Beacon, whether "Install Beacon" is offered, and what pressing it
 * does. iOS says it runs as the installed app with `navigator.standalone`.
 */
export function useInstallOffer() {
  const { standalone } = useViewport()
  const { prompt, installed } = useSyncExternalStore(subscribe, () => offer)
  const route = installRoute({
    ua: navigator.userAgent,
    standalone:
      standalone || installed || (navigator as { standalone?: boolean }).standalone === true,
    hasPrompt: prompt !== undefined,
    maxTouchPoints: navigator.maxTouchPoints,
  })
  return { route, offered: offersInstall(route), install }
}

/** "Install Beacon"'s icon: the browser's own on the desk, the home screen's on a phone. */
export const installIcon = (compact: boolean): LucideIcon =>
  compact ? SquarePlusIcon : MonitorDownIcon

/**
 * The sheet "Install Beacon" opens where the browser has no prompt: the Share sheet's two steps on
 * iOS, or, inside another app's browser, the line saying to open Beacon in Safari or Chrome. The
 * shell renders it once.
 */
export function InstallOfferSheet() {
  const { sheetOpen } = useSyncExternalStore(subscribe, () => offer)
  const { route } = useInstallOffer()
  return (
    <InstallSheet
      open={sheetOpen}
      onOpenChange={(open) => set({ sheetOpen: open })}
      inAppBrowser={route === "open-in-browser"}
    />
  )
}

/**
 * Home's one card offering to install Beacon, on the compact layout, wherever it can be: it goes
 * for good once the Member puts it away or installs, remembered in this browser.
 */
export function InstallCard() {
  const { compact } = useViewport()
  const { route } = useInstallOffer()
  const [dismissed, setDismissed] = useState(cardDismissed)
  if (!homeOffers({ route, compact, dismissed })) return null
  return (
    <InstallCallout
      inAppBrowser={route === "open-in-browser"}
      onInstall={install}
      onDismiss={() => {
        dismissCard()
        setDismissed(true)
      }}
    />
  )
}
